import React from 'react';
import { Form, useFormikContext, FormikConfig } from 'formik';
import isEqual from 'lodash/isEqual';
import { Box, Divider } from '@mui/material';
import { SessionFormField, SessionFormFieldDependency, SessionFormFieldError, SessionFormFieldsGroup } from '@uplift/api';
import { ServerFormSection, ServerFormSectionGrey } from './ServerFormSection.component';
import { ServerField } from './ServerField';
import { NestedFormik } from '../Form';
import { generateValidationSchema } from './ServerFormValidation';
import debounce from 'lodash/debounce';


const visibilityCompare = (fieldValue: unknown, conditionValue: unknown, condition: SessionFormFieldDependency['condition']) => {

  switch (condition) {
    case 'eq':
      return isEqual(fieldValue, conditionValue);
    case 'not_eq':
      return !isEqual(fieldValue, conditionValue);
    case 'in':
      if (Array.isArray(conditionValue)) {
        return conditionValue.includes(fieldValue);
      }

      return false;
    case 'not_in':
      if (Array.isArray(conditionValue)) {
        return !conditionValue.includes(fieldValue);
      }
      return false;
    default:
  }

  return false;
};


const Field: React.VFC<{ field: SessionFormField; disabled?: boolean; namespace?: string; nesting?: number }> = React.memo(({ field, namespace, disabled, nesting = 0 }) => {
  const formik = useFormikContext<any>();
  const name = namespace ? `${namespace}.${field.id}` : field.id;

  const shown = React.useMemo(() => {
    if (field.dependency) {
      const formikValue = namespace ? formik.values?.[namespace]?.[field.dependency.fieldId] : formik.values?.[field.dependency.fieldId];
      const shown = visibilityCompare(formikValue, field.dependency.value, field.dependency.condition);

      return shown;
    }

    // shown by default if `dependency` not provided
    return true;
  }, [ namespace, field.dependency, formik.values ]);

  if (field?.dependency?.behavior === 'hide' && !field.layout.whiteBackground) {
    if (shown) {
      return (
        <ServerFormSectionGrey raw={nesting > 0} {...field.layout}>
          <ServerField
            color="secondary"
            name={name}
            field={field}
            disabled={disabled}
          />
        </ServerFormSectionGrey>
      );
    }

    return null;
  }

  return (
    <ServerFormSection raw={nesting > 0} {...field.layout}>
      <ServerField
        color="secondary"
        name={name}
        field={field}
        disabled={!shown || disabled}
      />
    </ServerFormSection>
  );
});


const FieldsGroup: React.VFC<{ field: SessionFormFieldsGroup; disabled?: boolean; namespace?: string; nesting?: number }> = React.memo(({ field, namespace, disabled, nesting = 0 }) => {
  const formik = useFormikContext<any>();

  if (field.dependency) {
    const formikValue = namespace ? formik.values?.[namespace]?.[field.dependency.fieldId] : formik.values?.[field.dependency.fieldId];
    const shown = visibilityCompare(formikValue, field.dependency.value, field.dependency.condition);

    if (field.dependency?.behavior === 'hide' && !shown) {
      return null;
    }

    return (
      <ServerFormSectionGrey raw={nesting > 0}>
        {field.formFields.map(f => {
          if (f.fieldType === 'fields_group') {
            return <FieldsGroup namespace={namespace} key={f.id} field={f} nesting={nesting + 1} disabled={!shown || disabled} />;
          }
          return <Field namespace={namespace} key={f.label || f.id} field={f} nesting={nesting + 1} disabled={!shown || disabled} />;
        })}
      </ServerFormSectionGrey>
    );
  }

  return (
    <ServerFormSection raw={nesting > 0}>
      {field.formFields.map(f => {
        if (f.fieldType === 'fields_group') {
          return <FieldsGroup namespace={namespace} key={f.id} field={f} nesting={nesting + 1} disabled={disabled} />;
        }
        return <Field namespace={namespace} key={f.label || f.id} field={f} nesting={nesting + 1} disabled={disabled} />;
      })}
    </ServerFormSection>
  );
});


export const FormikAutoSave: React.VFC<{ onBeforeSave?: () => Promise<void>; onSave?: () => Promise<void> }> = ({ onBeforeSave, onSave }) => {
  const { values, initialValues, handleSubmit, submitCount } = useFormikContext();
  const onSaveRef = React.useRef(onSave);
  const onBeforeSaveRef = React.useRef(onBeforeSave);
  const submitCountRef = React.useRef(submitCount);

  onSaveRef.current = onSave;
  onBeforeSaveRef.current = onBeforeSave;
  submitCountRef.current = submitCount;

  React.useEffect(() => {
    if (!values) {
      return;
    }

    if (values === initialValues) {
      return;
    }

    const localSubmitCount = submitCountRef.current;
    const debouncedSave = debounce(async () => {
      if (localSubmitCount !== submitCountRef.current) {
        return;
      }

      if (onBeforeSaveRef.current) {
        await onBeforeSaveRef.current();
      }

      if (onSaveRef.current) {
        onSaveRef.current();
      } else {
        handleSubmit();
      }
    }, 1000);

    debouncedSave();

    return () => debouncedSave.cancel();
  }, [ values, initialValues, handleSubmit ]);

  return null;
};


export type ServerFormProps = {
  namespace?: string;
  fields: (SessionFormField | SessionFormFieldsGroup)[];
  autoSave?: boolean;
  disabled?: boolean;
};


export const ServerForm: React.VFC<ServerFormProps> = React.memo(({ fields, namespace, autoSave, disabled }) => {
  return (
    <Form>
      {fields.map(field => {
        if (field.fieldType === 'divider') {
          return <Box py={1} key={field.id}><Divider /></Box>;
        }

        if (field.fieldType === 'fields_group') {
          const group = field;

          return <FieldsGroup namespace={namespace} key={group.id} field={group} disabled={disabled} />;
        }

        return <Field namespace={namespace} key={field.label || field.id} field={field} disabled={disabled} />;
      })}
      {autoSave ? <FormikAutoSave /> : null}
    </Form>
  );
});


export const extractServerFormValues = (fields: (SessionFormField | SessionFormFieldsGroup)[]): Record<string, unknown> => {
  let result: Record<string, unknown> = {};

  for (const field of fields) {
    if (field.fieldType === 'divider') {
      continue;
    } else if (field.fieldType === 'fields_group') {
      result = { ...result, ...extractServerFormValues(field.formFields) };
    } else {
      result[field.id] = field.value ?? field.defaultValue;
    }
  }

  return result;
};


export type ServerFormFormikProps = ServerFormProps & {
  errors?: SessionFormFieldError[];
  enableReinitialize?: FormikConfig<Record<string, unknown>>['enableReinitialize'];
  onSubmit: FormikConfig<Record<string, unknown>>['onSubmit'];
  children: FormikConfig<Record<string, unknown>>['children'];
  initialValues?: Record<string, unknown>;
  frontendValidation?: boolean;
} & Pick<FormikConfig<unknown>, 'validationSchema' | 'validateOnBlur' | 'validateOnChange' | 'validateOnMount' | 'initialErrors'>;


export const ServerFormFormik: React.FC<ServerFormFormikProps & { formName?: string }> = ({
  validateOnBlur = false,
  validateOnChange = false,
  validateOnMount = false,
  frontendValidation = false,
  ...props
}) => {
  const initialValues = React.useMemo(() => ({ ...extractServerFormValues(props.fields), ...props.initialValues }), [ props.fields, props.initialValues ]);
  const initialErrors = React.useMemo(() => {
    return props.errors?.reduce((prev, curr) => ({
      ...prev,
      [curr.id]: curr.message,
    }), { ...props.initialErrors } as Record<string, string>);
  }, [ props.errors, props.initialErrors ]);
  const initialTouched = React.useMemo(() => {
    if (!initialErrors) {
      return {};
    }

    return Object.keys(initialErrors).reduce((prev, curr) => ({ ...prev, [curr]: !!initialErrors[curr] }), {} as Record<string, boolean>);
  }, [ initialErrors ]);

  const validationProps = React.useMemo(() => {
    if (frontendValidation) {
      const validationSchema = generateValidationSchema(props.fields ?? []);

      return {
        validateOnBlur: true,
        validationSchema: validationSchema,
      };
    }
    return {};
  }, [ props.fields, frontendValidation ]);

  return (
    <NestedFormik
      name={props.formName}
      enableReinitialize={props.enableReinitialize}
      initialValues={initialValues}
      initialErrors={initialErrors}
      initialTouched={initialTouched}
      onSubmit={props.onSubmit}
      validateOnBlur={validateOnBlur}
      validateOnChange={validateOnChange}
      validateOnMount={validateOnMount}
      validationSchema={props.validationSchema}
      {...validationProps}
    >
      {props.children}
    </NestedFormik>
  );
};
